<template>
  <Teleport to="body">
    <v-dialog
      :model-value="modelValue"
      max-width="480"
      persistent
      class="caller-dialog-root"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <SnackbarComp :SnackbarComponent="snackbar" />

      <div class="caller-dialog" role="dialog" aria-modal="true">
        <header class="caller-dialog__header">
          <div class="caller-dialog__brand">
            <div class="caller-dialog__icon" aria-hidden="true">
              <v-icon :icon="isCreate ? 'mdi-account-plus-outline' : 'mdi-account-edit-outline'" size="22" color="white" />
            </div>
            <div>
              <h2 class="caller-dialog__title">{{ isCreate ? 'Add caller' : 'Update caller' }}</h2>
              <p class="caller-dialog__subtitle">
                {{ isCreate ? 'Register a new contact for your AI call agent.' : 'Update caller details for your organization.' }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="caller-dialog__close"
            aria-label="Close"
            :disabled="saving"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="caller-dialog__body">
          <v-form ref="formRef" v-model="isValid">
            <label class="caller-field">
              <span class="caller-field__label">Caller name</span>
              <v-text-field
                v-model="form.userName"
                placeholder="Enter caller name"
                prepend-inner-icon="mdi-account-outline"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                :rules="userNameRules"
                class="caller-field__input"
              />
            </label>

            <div class="caller-field-row">
              <label class="caller-field caller-field--code">
                <span class="caller-field__label">Country code</span>
                <v-autocomplete
                  v-model="form.countryCode"
                  :items="countryList"
                  item-title="dial_code"
                  item-value="dial_code"
                  prepend-inner-icon="mdi-earth"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  :disabled="!isCreate"
                  :rules="[(v) => (v ? true : 'Required')]"
                  class="caller-field__input"
                />
              </label>

              <label class="caller-field caller-field--number">
                <span class="caller-field__label">Contact number</span>
                <v-text-field
                  v-model="form.contactNumber"
                  placeholder="10-digit number"
                  prepend-inner-icon="mdi-phone-outline"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  maxlength="10"
                  :disabled="!isCreate"
                  :rules="contactNumberRules"
                  class="caller-field__input"
                />
              </label>
            </div>
          </v-form>
        </div>

        <footer class="caller-dialog__footer">
          <v-btn
            variant="text"
            class="caller-dialog__cancel"
            :disabled="saving"
            @click="close"
          >
            Cancel
          </v-btn>
          <v-btn
            variant="flat"
            class="caller-dialog__submit"
            :loading="saving"
            :disabled="!isValid"
            @click="submit"
          >
            {{ isCreate ? 'Add caller' : 'Save changes' }}
          </v-btn>
        </footer>
      </div>
    </v-dialog>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import countryCodeList from '@/JsonFiles/CountryCodeList.json'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { generateClient } from 'aws-amplify/api'
import { addUniqueCaller, editOrgCaller } from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'AddCallersDialog' })

const client = generateClient()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  caller: { type: Object, default: null },
  mode: { type: String, default: 'CREATE' },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const store = useStore()
const formRef = ref(null)
const saving = ref(false)
const isValid = ref(false)
const snackbar = ref({})

const form = ref({
  userName: '',
  countryCode: '+91',
  contactNumber: '',
})

const countryList = ref(countryCodeList)

const isCreate = computed(() => props.mode === 'CREATE')

const userNameRules = [
  (v) => (v != null && v.trim() !== '' ? true : 'Caller name is required'),
  (v) => (v && v.trim().length >= 3 ? true : 'Name must be at least 3 characters'),
  (v) =>
    !/\s/.test(String(v ?? ''))
      ? true
      : 'Caller name must be a single word with no spaces',
]

const contactNumberRules = [
  (v) => !!v || 'Contact number is required',
  (v) => /^\d{10}$/.test(v) || 'Contact number must be 10 digits',
]

function resetForm() {
  form.value = {
    userName: '',
    countryCode: '+91',
    contactNumber: '',
  }
  formRef.value?.resetValidation?.()
}

function populateForm() {
  if (!isCreate.value && props.caller) {
    form.value.userName = props.caller.caller_name || ''
    form.value.countryCode = props.caller.country_code || '+91'
    const id = String(props.caller.caller_id || '').replace(/\D/g, '')
    form.value.contactNumber = id.length >= 10 ? id.slice(-10) : id
    return
  }
  resetForm()
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) populateForm()
  },
)

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  const result = await formRef.value?.validate?.()
  if (result === false) return

  const { org_id } = store.getters.sessionContext ?? {}
  if (!org_id) return

  saving.value = true
  try {
    const callerName = form.value.userName.trim()
    const input = isCreate.value
      ? {
          org_id,
          caller_name: callerName,
          country_code: form.value.countryCode,
          caller_contact_number: form.value.contactNumber,
        }
      : {
          org_id,
          caller_id: String(props.caller?.caller_id || '').trim(),
          caller_name: callerName,
        }

    const mutation = isCreate.value ? addUniqueCaller : editOrgCaller
    const responseKey = isCreate.value ? 'addUniqueCaller' : 'editOrgCaller'

    const { data, errors } = await client.graphql({
      query: mutation,
      variables: { input },
    })

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    const response = parseAwsJson(data?.[responseKey])

    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(response, 'Something went wrong'),
      }
      return
    }

    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText: isCreate.value
        ? 'Caller added successfully'
        : 'Caller updated successfully',
    }
    emit('saved')
    close()
  } catch (err) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: getApiMessage(err, 'Something went wrong'),
    }
  } finally {
    saving.value = false
  }
}
</script>
